/**
 * BookingDetailsDialog.tsx
 * Comprehensive booking details view dialog
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  XMarkIcon,
  UserIcon,
  CalendarDaysIcon,
  CurrencyEuroIcon,
  TruckIcon,
  ChatBubbleBottomCenterTextIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  XCircleIcon,
  PencilIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { adminService } from '../../services/adminService';
import { format } from 'date-fns';
import './BookingDetailsDialog.css';

interface BookingForDetails {
  id: string;
  bookingNumber: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  tripDate: string;
  tripTime: string;
  tripType: string;
  participantCount: number;
  totalAmount: number;
  paidAmount: number;
  status: string;
  paymentStatus: string;
  createdAt: string;
  updatedAt: string;
  extras?: string[];
  specialRequests?: string;
  notes?: string[];
  quoteValidUntil?: string;
  [key: string]: any;
}

interface BookingNote {
  id: string;
  text: string;
  type: string;
  createdBy: string;
  createdAt: string;
  isReminder: boolean;
  reminderDate?: string;
}

interface BookingDetailsDialogProps {
  booking: BookingForDetails | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit: () => void;
  onShowNotes: () => void;
}

const BookingDetailsDialog: React.FC<BookingDetailsDialogProps> = ({
  booking,
  isOpen,
  onClose,
  onEdit,
  onShowNotes
}) => {
  const navigate = useNavigate();
  const [notes, setNotes] = useState<BookingNote[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (isOpen && booking) {
      loadNotes();
      setActionMessage(null);
    }
  }, [isOpen, booking]);

  // Auto-hide action message after 4 seconds
  useEffect(() => {
    if (actionMessage) {
      const timer = setTimeout(() => setActionMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [actionMessage]);

  if (!isOpen || !booking) return null;

  // Helper für deutschen Status-Text
  const getStatusLabel = (status: string): string => {
    const normalizedStatus = status?.toLowerCase() || '';
    const statusLabels: Record<string, string> = {
      'pending': 'Ausstehend',
      'confirmed': 'Bestätigt',
      'completed': 'Abgeschlossen',
      'cancelled': 'Storniert',
      'option': 'Option'
    };
    return statusLabels[normalizedStatus] || status;
  };

  // Helper für deutschen Zahlungsstatus-Text
  const getPaymentStatusLabel = (status: string): string => {
    const normalizedStatus = status?.toLowerCase() || '';
    const paymentLabels: Record<string, string> = {
      'pending': 'Ausstehend',
      'partial': 'Teilbezahlt',
      'paid': 'Bezahlt',
      'refunded': 'Erstattet'
    };
    return paymentLabels[normalizedStatus] || status;
  };

  // Handle send email
  const handleSendEmail = async () => {
    setActionLoading('email');
    setActionMessage(null);
    try {
      const statusLabel = getStatusLabel(booking.status);
      const paymentStatusLabel = getPaymentStatusLabel(booking.paymentStatus);

      // E-Mail-Betreff mit Status
      const subject = encodeURIComponent(`Ihre Buchung ${booking.bookingNumber} - ${statusLabel} | Frankenfloss`);

      // Vollständiger deutscher E-Mail-Text
      const body = encodeURIComponent(
        `Sehr geehrte/r ${booking.customerName},\n\n` +
        `nachfolgend finden Sie die Details zu Ihrer Buchung bei Frankenfloss.\n\n` +
        `═══════════════════════════════════════\n` +
        `BUCHUNGSDETAILS\n` +
        `═══════════════════════════════════════\n\n` +
        `Buchungsnummer: ${booking.bookingNumber}\n` +
        `Buchungsstatus: ${statusLabel}\n` +
        `Zahlungsstatus: ${paymentStatusLabel}\n\n` +
        `───────────────────────────────────────\n` +
        `FAHRTDETAILS\n` +
        `───────────────────────────────────────\n\n` +
        `Datum: ${new Date(booking.tripDate).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}\n` +
        `Uhrzeit: ${booking.tripTime} Uhr\n` +
        `Tour-Typ: ${booking.tripType}\n` +
        `Teilnehmer: ${booking.participantCount} Personen\n\n` +
        `───────────────────────────────────────\n` +
        `ZAHLUNGSINFORMATIONEN\n` +
        `───────────────────────────────────────\n\n` +
        `Gesamtbetrag: ${formatCurrency(booking.totalAmount)}\n` +
        `Bereits bezahlt: ${formatCurrency(booking.paidAmount)}\n` +
        `Offener Betrag: ${formatCurrency(booking.totalAmount - booking.paidAmount)}\n\n` +
        `═══════════════════════════════════════\n\n` +
        `Bei Fragen stehen wir Ihnen gerne zur Verfügung.\n\n` +
        `Mit freundlichen Grüßen\n` +
        `Ihr Frankenfloss-Team\n\n` +
        `---\n` +
        `Diese E-Mail wurde automatisch erstellt.`
      );
      window.open(`mailto:${booking.customerEmail}?subject=${subject}&body=${body}`);
      setActionMessage({ type: 'success', text: 'E-Mail-Client geöffnet' });
    } catch (err) {
      console.error('Error opening email:', err);
      setActionMessage({ type: 'error', text: 'Fehler beim Öffnen des E-Mail-Clients' });
    } finally {
      setActionLoading(null);
    }
  };

  // Handle invoice - navigate to InvoiceManagement with booking filter
  const handleGoToInvoices = () => {
    // Close dialog and navigate to invoice management with booking number as search
    onClose();
    navigate('/admin/invoices', { state: { searchBookingNumber: booking.bookingNumber } });
  };

  const loadNotes = async () => {
    setLoadingNotes(true);
    try {
      const response = await adminService.getBookingNotes(parseInt(booking.id));
      const transformedNotes = (Array.isArray(response) ? response : []).map((dto: any) => ({
        id: dto.id.toString(),
        text: dto.text,
        type: dto.type,
        createdBy: dto.createdBy,
        createdAt: dto.createdAt,
        isReminder: dto.isReminder,
        reminderDate: dto.reminderDate
      }));
      setNotes(transformedNotes);
    } catch (err) {
      console.error('Error loading notes:', err);
      setNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr: string) => {
    return format(new Date(dateStr), 'dd.MM.yyyy HH:mm');
  };

  const getStatusBadge = (status: string) => {
    // Case-insensitive lookup für Status
    const normalizedStatus = status?.toLowerCase() || '';
    const statusMap: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
      'pending': { label: 'Ausstehend', className: 'status-pending', icon: <ClockIcon /> },
      'confirmed': { label: 'Bestätigt', className: 'status-confirmed', icon: <CheckCircleIcon /> },
      'completed': { label: 'Abgeschlossen', className: 'status-completed', icon: <CheckCircleIcon /> },
      'cancelled': { label: 'Storniert', className: 'status-cancelled', icon: <XCircleIcon /> },
      'option': { label: 'Option', className: 'status-option', icon: <ClockIcon /> }
    };
    return statusMap[normalizedStatus] || { label: status, className: 'status-default', icon: <InformationCircleIcon /> };
  };

  const getPaymentBadge = (status: string) => {
    const paymentMap: Record<string, { label: string; className: string }> = {
      'pending': { label: 'Ausstehend', className: 'payment-pending' },
      'partial': { label: 'Teilbezahlt', className: 'payment-partial' },
      'paid': { label: 'Bezahlt', className: 'payment-paid' },
      'refunded': { label: 'Erstattet', className: 'payment-refunded' }
    };
    return paymentMap[status] || { label: status, className: 'payment-default' };
  };

  const statusBadge = getStatusBadge(booking.status);
  const paymentBadge = getPaymentBadge(booking.paymentStatus);
  const openAmount = booking.totalAmount - booking.paidAmount;

  return (
    <div className="details-dialog-overlay" onClick={onClose}>
      <div className="details-dialog" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="details-dialog-header">
          <div className="details-header-content">
            <h3>{booking.bookingNumber}</h3>
            <div className="details-header-badges">
              <span className={`details-status-badge ${statusBadge.className}`}>
                {statusBadge.icon}
                {statusBadge.label}
              </span>
              <span className={`details-payment-badge ${paymentBadge.className}`}>
                {paymentBadge.label}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="details-dialog-close">
            <XMarkIcon />
          </button>
        </div>

        {/* Body */}
        <div className="details-dialog-body">
          {/* Main Grid */}
          <div className="details-grid">
            {/* Customer Info */}
            <div className="details-card">
              <div className="details-card-header">
                <UserIcon />
                <h4>Kundeninformationen</h4>
              </div>
              <div className="details-card-body">
                <div className="details-row">
                  <span className="details-label">Name</span>
                  <span className="details-value">{booking.customerName}</span>
                </div>
                <div className="details-row">
                  <span className="details-label">E-Mail</span>
                  <a href={`mailto:${booking.customerEmail}`} className="details-value details-link">
                    {booking.customerEmail}
                  </a>
                </div>
                <div className="details-row">
                  <span className="details-label">Telefon</span>
                  <span className="details-value">{booking.customerPhone || '-'}</span>
                </div>
              </div>
            </div>

            {/* Trip Info */}
            <div className="details-card">
              <div className="details-card-header">
                <CalendarDaysIcon />
                <h4>Fahrtdetails</h4>
              </div>
              <div className="details-card-body">
                <div className="details-row">
                  <span className="details-label">Datum</span>
                  <span className="details-value">{formatDate(booking.tripDate)}</span>
                </div>
                <div className="details-row">
                  <span className="details-label">Uhrzeit</span>
                  <span className="details-value">{booking.tripTime} Uhr</span>
                </div>
                <div className="details-row">
                  <span className="details-label">Tour-Typ</span>
                  <span className="details-value details-tour-badge">{booking.tripType}</span>
                </div>
                <div className="details-row">
                  <span className="details-label">Teilnehmer</span>
                  <span className="details-value">{booking.participantCount} Personen</span>
                </div>
              </div>
            </div>

            {/* Payment Info */}
            <div className="details-card">
              <div className="details-card-header">
                <CurrencyEuroIcon />
                <h4>Zahlungsinformationen</h4>
              </div>
              <div className="details-card-body">
                <div className="details-row">
                  <span className="details-label">Gesamtbetrag</span>
                  <span className="details-value details-amount">{formatCurrency(booking.totalAmount)}</span>
                </div>
                <div className="details-row">
                  <span className="details-label">Bezahlt</span>
                  <span className="details-value details-amount-paid">{formatCurrency(booking.paidAmount)}</span>
                </div>
                <div className="details-row">
                  <span className="details-label">Offen</span>
                  <span className={`details-value ${openAmount > 0 ? 'details-amount-open' : 'details-amount-paid'}`}>
                    {formatCurrency(openAmount)}
                  </span>
                </div>
              </div>
            </div>

            {/* Booking Meta */}
            <div className="details-card">
              <div className="details-card-header">
                <ClockIcon />
                <h4>Buchungsdaten</h4>
              </div>
              <div className="details-card-body">
                <div className="details-row">
                  <span className="details-label">Erstellt</span>
                  <span className="details-value">{formatDateTime(booking.createdAt)}</span>
                </div>
                <div className="details-row">
                  <span className="details-label">Aktualisiert</span>
                  <span className="details-value">{formatDateTime(booking.updatedAt)}</span>
                </div>
                {booking.quoteValidUntil && (
                  <div className="details-row">
                    <span className="details-label">Option bis</span>
                    <span className="details-value">{formatDate(booking.quoteValidUntil)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Special Requests */}
          {booking.specialRequests && (
            <div className="details-section">
              <div className="details-section-header">
                <InformationCircleIcon />
                <h4>Besondere Wünsche</h4>
              </div>
              <div className="details-section-content">
                <p>{booking.specialRequests}</p>
              </div>
            </div>
          )}

          {/* Notes Preview */}
          <div className="details-section">
            <div className="details-section-header">
              <ChatBubbleBottomCenterTextIcon />
              <h4>Notizen</h4>
              <span className="details-section-count">{notes.length}</span>
              <button onClick={onShowNotes} className="details-section-link">
                Alle anzeigen
              </button>
            </div>
            <div className="details-section-content">
              {loadingNotes ? (
                <div className="details-notes-loading">
                  <div className="details-spinner"></div>
                </div>
              ) : notes.length === 0 ? (
                <p className="details-notes-empty">Keine Notizen vorhanden</p>
              ) : (
                <div className="details-notes-preview">
                  {notes.slice(0, 3).map((note) => (
                    <div key={note.id} className="details-note-item">
                      <span className={`details-note-badge details-note-${note.type}`}>
                        {note.type === 'internal' ? 'Intern' : note.type === 'customer' ? 'Kunde' : 'System'}
                      </span>
                      <p className="details-note-text">{note.text}</p>
                      <span className="details-note-meta">
                        {note.createdBy} - {formatDateTime(note.createdAt)}
                      </span>
                    </div>
                  ))}
                  {notes.length > 3 && (
                    <button onClick={onShowNotes} className="details-notes-more">
                      +{notes.length - 3} weitere Notizen
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Message */}
        {actionMessage && (
          <div className={`details-action-message ${actionMessage.type}`}>
            {actionMessage.type === 'success' ? <CheckCircleIcon /> : <ExclamationCircleIcon />}
            <span>{actionMessage.text}</span>
          </div>
        )}

        {/* Footer */}
        <div className="details-dialog-footer">
          <button onClick={onEdit} className="details-btn-primary" disabled={!!actionLoading}>
            <PencilIcon />
            Bearbeiten
          </button>
          <button onClick={onShowNotes} className="details-btn-secondary" disabled={!!actionLoading}>
            <ChatBubbleBottomCenterTextIcon />
            Notizen
          </button>
          <button
            onClick={handleSendEmail}
            className="details-btn-secondary"
            disabled={!!actionLoading}
          >
            {actionLoading === 'email' ? (
              <div className="details-btn-spinner"></div>
            ) : (
              <EnvelopeIcon />
            )}
            E-Mail senden
          </button>
          <button
            onClick={handleGoToInvoices}
            className="details-btn-secondary"
            disabled={!!actionLoading}
          >
            <DocumentTextIcon />
            Rechnungen
          </button>
          <button
            onClick={async () => {
              setActionLoading('ticket');
              try {
                await adminService.downloadBoardingTicketPdf(parseInt(booking.id), booking.bookingNumber);
                setActionMessage({ type: 'success', text: 'Bordticket heruntergeladen' });
              } catch (err) {
                setActionMessage({ type: 'error', text: 'Bordticket konnte nicht erstellt werden' });
              } finally {
                setActionLoading(null);
              }
            }}
            className="details-btn-secondary"
            disabled={!!actionLoading}
          >
            {actionLoading === 'ticket' ? (
              <div className="details-btn-spinner"></div>
            ) : (
              <DocumentTextIcon />
            )}
            Bordticket
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingDetailsDialog;
